import { getCampaigns } from 'actions/campains'
import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useInternalAdmin } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'
import contentApi from 'services/api/content'

import {
  buildStepMetrics,
  daysToHours,
  EMPTY_STEP_METRICS,
  formatCount,
  formatTimingLabel,
  hoursToDays,
  interpolateCampaignPreview,
  OTA_BUSTER_CAMPAIGN_STEPS,
  OTA_BUSTER_CONTENT_CATEGORIES,
  OTA_BUSTER_ENTRY
} from './campaignFlowMock'
import CampaignStepCard from './CampaignStepCard'
import EditStepDialog from './EditStepDialog'
import styles from './OtaBuster.module.scss'
import SavibookPreviewModal from './SavibookPreviewModal'

function GuestsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M6.5 8a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM12.75 8.5a2 2 0 100-4 2 2 0 000 4zM3 14.25c0-1.933 1.567-3.5 3.5-3.5h.5c1.933 0 3.5 1.567 3.5 3.5V15H3v-.75zM11.25 12c.69 0 1.326.212 1.857.573A3.48 3.48 0 0115 14.25V15h-2.5v-.75c0-.86-.28-1.655-.75-2.3.39-.3.86-.45 1.25-.45z"
        fill="currentColor"
      />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M6 2.5A1.5 1.5 0 004.5 4v12A1.5 1.5 0 006 17.5h8a1.5 1.5 0 001.5-1.5V7.121a1.5 1.5 0 00-.44-1.06l-3.12-3.122A1.5 1.5 0 0010.878 2.5H6z"
        stroke="currentColor"
        strokeWidth="1.4"
        fill="none"
      />
      <path d="M10.5 3v3.5A1 1 0 0011.5 7.5H15" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function OtaBuster() {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const isInternalAdmin = useInternalAdmin()
  const hotelId = useSelector(selectCurrentHotelId)
  const hotelNameFromStore = useSelector(selectCurrentHotelName)
  const isDemoMode = useSelector(s => s.login.isDemoMode)
  const campaigns = useSelector(s => s.campaigns.content[hotelId])
  const isLoadingCampaigns = useSelector(s => s.campaigns.isLoadingCampaigns)

  const [previewStep, setPreviewStep] = useState(null)
  const [editingStepId, setEditingStepId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isEnsuringContents, setIsEnsuringContents] = useState(false)
  const [stepOverrides, setStepOverrides] = useState({})
  const [analytics, setAnalytics] = useState(null)
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true)

  const hotelName = isDemoMode
    ? 'The Luxury Resort & Spa'
    : hotelNameFromStore || 'the hotel'
  const firstName = 'Joe'

  useEffect(() => {
    if (!hotelId) {
      return undefined
    }

    let cancelled = false

    const loadAndEnsureContents = async () => {
      try {
        const list = await campaignApi.getList(hotelId)
        if (cancelled) return

        const items = list?.items || []
        const present = new Set(
          items
            .filter(campaign => OTA_BUSTER_CONTENT_CATEGORIES.includes(campaign.category))
            .map(campaign => campaign.category)
        )
        const missing = OTA_BUSTER_CONTENT_CATEGORIES.some(category => !present.has(category))

        if (missing) {
          setIsEnsuringContents(true)
          await campaignApi.ensureOtaBusterCampaignContents()
        }

        if (!cancelled) {
          await dispatch(getCampaigns(hotelId))
        }
      } catch (error) {
        console.error('Failed to ensure OTA Buster campaign contents:', error)
        if (!cancelled) {
          addToast('Could not create OTA Buster email content for this hotel', {
            appearance: 'error',
            autoDismiss: true
          })
          dispatch(getCampaigns(hotelId))
        }
      } finally {
        if (!cancelled) {
          setIsEnsuringContents(false)
        }
      }
    }

    loadAndEnsureContents()

    return () => {
      cancelled = true
    }
  }, [addToast, dispatch, hotelId])

  const fetchAnalytics = useCallback(async () => {
    if (!hotelId) {
      return
    }

    setIsLoadingAnalytics(true)
    try {
      const data = await contentApi.getOtaBusterAnalytics()
      setAnalytics(data)
    } catch (error) {
      console.error('Failed to fetch OTA Buster analytics:', error)
      setAnalytics(null)
    } finally {
      setIsLoadingAnalytics(false)
    }
  }, [hotelId])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const stepMetrics = useMemo(() => buildStepMetrics(analytics), [analytics])

  const otaCampaignsByCategory = useMemo(() => {
    const map = {}
    ;(campaigns || [])
      .filter(campaign => OTA_BUSTER_CONTENT_CATEGORIES.includes(campaign.category))
      .forEach(campaign => {
        map[campaign.category] = campaign
      })
    return map
  }, [campaigns])

  const getStepState = useCallback(
    step => {
      const campaign = otaCampaignsByCategory[step.contentCategory]
      const override = stepOverrides[step.id] || {}
      const daysBeforeCheckIn =
        override.daysBeforeCheckIn ??
        hoursToDays(campaign?.hoursAfterPreviousCampaign, step.defaultDaysBeforeCheckIn)

      return {
        isActive:
          override.isActive ??
          (campaign?.isActive == null ? true : Boolean(campaign.isActive)),
        hasEmailCampaign:
          campaign?.hasEmailCampaign == null ? true : Boolean(campaign.hasEmailCampaign),
        contentId: campaign?.contentId ?? campaign?.notificationContentId ?? null,
        messageTemplate: campaign?.messageTemplate || step.preview,
        daysBeforeCheckIn
      }
    },
    [otaCampaignsByCategory, stepOverrides]
  )

  const persistStepConfiguration = async (step, data) => {
    const state = getStepState(step)
    if (!state.contentId) {
      setStepOverrides(current => ({
        ...current,
        [step.id]: {
          isActive: data.isEnabled,
          daysBeforeCheckIn: data.daysBeforeCheckIn
        }
      }))
      return
    }

    await contentApi.updateConfiguration(state.contentId, {
      messageTemplate: state.messageTemplate,
      isActive: data.isEnabled,
      medium: 'Email',
      hoursAfterPreviousCampaign:
        step.timingKind === 'daysBeforeCheckIn'
          ? daysToHours(data.daysBeforeCheckIn)
          : undefined,
      messageMedia: null
    })

    dispatch(getCampaigns(hotelId))
  }

  const handleDialogSave = async data => {
    const step = OTA_BUSTER_CAMPAIGN_STEPS.find(item => item.id === editingStepId)
    if (!step) return

    setIsSaving(true)
    try {
      await persistStepConfiguration(step, data)
      setStepOverrides(current => ({
        ...current,
        [step.id]: {
          isActive: data.isEnabled,
          daysBeforeCheckIn: data.daysBeforeCheckIn
        }
      }))
      addToast('Configuration saved successfully', {
        appearance: 'success',
        autoDismiss: true
      })
      setEditingStepId(null)
    } catch (error) {
      console.error('Failed to save OTA Buster step configuration:', error)
      addToast('Failed to save configuration', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleEnableStep = async step => {
    try {
      await persistStepConfiguration(step, {
        isEnabled: true,
        daysBeforeCheckIn: getStepState(step).daysBeforeCheckIn
      })
      setStepOverrides(current => ({
        ...current,
        [step.id]: {
          ...(current[step.id] || {}),
          isActive: true
        }
      }))
      addToast(`${step.title} enabled`, {
        appearance: 'success',
        autoDismiss: true
      })
    } catch (error) {
      console.error('Failed to enable OTA Buster step:', error)
      addToast('Failed to enable step', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const editingStep = OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.id === editingStepId)
  const editingState = editingStep ? getStepState(editingStep) : null

  if (!isInternalAdmin) {
    return (
      <div className={styles.accessDenied} data-testid="ota-buster-access-denied">
        Access denied. OTA Buster is available to internal admins only.
      </div>
    )
  }

  return (
    <div className={styles.page} data-testid="ota-buster-campaign-flow">
      <CustomHeader width={960}>
        <div className={styles.header}>
          <span className={styles.headerRoot}>OTA Buster</span>
          <span className={styles.headerSep}>{'>'}</span>
          <span>Campaign flow</span>
        </div>
      </CustomHeader>

      <div className={styles.wrapper}>
        <div className={styles.settings}>
          <div className={styles.sectionLabel}>Settings</div>

          <div
            className={`${styles.upgradeCard} ${styles.upgradeCardLocked}`}
            data-testid="ota-buster-room-upgrade-locked"
            aria-disabled="true"
          >
            <div className={styles.upgradeToggle}>
              <span className={styles.switchTrack} aria-hidden="true" />
              <span className={styles.upgradeText}>
                Enable Room Upgrade — lets you set an upsell link inside the pre-check-in
                flow.
              </span>
            </div>
            <span className={styles.comingSoonBadge}>Coming Soon</span>
          </div>

          <div className={styles.infoBar} role="note">
            Steps are only sent when they still apply. If a guest has already checked in,
            that message is skipped automatically. Each step can be enabled or disabled on
            its own — turning one off does not turn off the others.
          </div>
        </div>

        <div className={styles.sectionLabel}>Campaign flow</div>

        {isLoadingCampaigns || isEnsuringContents || isLoadingAnalytics ? (
          <LoadingIndicator />
        ) : (
          <>
            <div className={styles.entryCard}>
              <div className={styles.entryLeft}>
                <span className={styles.entryIcon}>
                  <DocumentIcon />
                </span>
                <span className={styles.entryTitle}>{OTA_BUSTER_ENTRY.title}</span>
              </div>
              <div className={styles.entryRight}>
                <GuestsIcon />
                <span>{formatCount(analytics?.otaBookedGuests)} guests</span>
              </div>
            </div>

            <div className={styles.flow}>
              {OTA_BUSTER_CAMPAIGN_STEPS.map(step => {
                const state = getStepState(step)
                return (
                  <div key={step.id} className={styles.flowItem}>
                    <div className={styles.timingRow}>
                      <button
                        type="button"
                        className={styles.timingPill}
                        onClick={() => setEditingStepId(step.id)}
                      >
                        {formatTimingLabel(step, state.daysBeforeCheckIn)}
                      </button>
                    </div>
                    <CampaignStepCard
                      step={step}
                      metrics={stepMetrics[step.id] || EMPTY_STEP_METRICS}
                      preview={interpolateCampaignPreview(
                        state.messageTemplate.includes('{first}')
                          ? state.messageTemplate
                          : step.preview,
                        {
                          firstName,
                          hotelName
                        }
                      )}
                      isActive={state.isActive}
                      hasEmailCampaign={state.hasEmailCampaign}
                      onEdit={() => setEditingStepId(step.id)}
                      onEnable={() => handleEnableStep(step)}
                      onPreviewSavibook={setPreviewStep}
                    />
                  </div>
                )
              })}
            </div>
          </>
        )}

      </div>

      {previewStep && (
        <SavibookPreviewModal
          step={previewStep}
          hotelName={hotelName}
          firstName={firstName}
          onClose={() => setPreviewStep(null)}
        />
      )}

      {editingStep && (
        <EditStepDialog
          key={editingStep.id}
          contentId={editingState.contentId}
          contentCategory={editingStep.contentCategory}
          title={`Edit ${editingStep.title}`}
          onClose={() => setEditingStepId(null)}
          onSave={handleDialogSave}
          isSaving={isSaving}
          timingKind={editingStep.timingKind}
          timingLabel={formatTimingLabel(editingStep, editingState.daysBeforeCheckIn)}
          initialData={{
            isEnabled: editingState.isActive,
            daysBeforeCheckIn:
              editingState.daysBeforeCheckIn || editingStep.defaultDaysBeforeCheckIn || 1
          }}
        />
      )}
    </div>
  )
}

export default OtaBuster
